import * as THREE from '../../libs/three.js/build/three.module.js';
import { Line2 } from '../../libs/three.js/lines/Line2.js';
import { LineGeometry } from '../../libs/three.js/lines/LineGeometry.js';
import { LineMaterial } from '../../libs/three.js/lines/LineMaterial.js';
import { Annotation } from '../Annotation.js';
import { Utils } from '../utils.js';
import { CameraMode } from '../defines.js';
import { EventDispatcher } from '../EventDispatcher.js';

export class AnnotationTool extends EventDispatcher {
  constructor(viewer) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;

    // Scene with lights for annotation handle spheres (same pattern as MeasuringTool)
    this.scene = new THREE.Scene();
    this.scene.name = 'scene_annotation';
    this.light = new THREE.DirectionalLight(0xffffff);
    this.ambientLight = new THREE.AmbientLight(0x222222);
    this.lightTargetObject = new THREE.Object3D();
    this.scene.add(this.light);
    this.scene.add(this.ambientLight);
    this.scene.add(this.lightTargetObject);
    this.light.target = this.lightTargetObject;

    this.viewer.inputHandler.registerInteractiveScene(this.scene);

    // Map: annotation → { sphere, line }
    this.handles = new Map();
    this.sphereGeometry = new THREE.SphereGeometry(0.4, 10, 10);

    // Listen for annotation add/remove
    const annotations = viewer.scene.annotations;

    this.onAdd = (e) => {
      this.addHandle(e.annotation);
    };
    this.onRemove = (e) => {
      this.removeHandle(e.annotation);
    };

    annotations.addEventListener('annotation_added', this.onAdd);
    annotations.addEventListener('annotation_removed', this.onRemove);

    // Add handles for existing annotations
    for (const annotation of annotations.children) {
      this.addHandle(annotation);
    }

    viewer.addEventListener('update', this.update.bind(this));
    viewer.addEventListener('render.pass.perspective_overlay', this.render.bind(this));
    viewer.addEventListener('scene_changed', (e) => {
      e.oldScene?.annotations.removeEventListener('annotation_added', this.onAdd);
      e.oldScene?.annotations.removeEventListener('annotation_removed', this.onRemove);
      e.scene.annotations.addEventListener('annotation_added', this.onAdd);
      e.scene.annotations.addEventListener('annotation_removed', this.onRemove);
    });
  }

  addHandle(annotation) {
    if (this.handles.has(annotation)) return;

    // Visible sphere (same style as measurement spheres)
    const sphere = new THREE.Mesh(
      this.sphereGeometry,
      new THREE.MeshLambertMaterial({
        color: 0x555555,
        emissive: 0x000000,
      })
    );
    sphere.position.copy(annotation.position);
    this.scene.add(sphere);

    // Connecting line from annotation point to label (when offset is non-zero)
    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions([0, 0, 0, 0, 0, 0]);
    const lineMaterial = new LineMaterial({
      color: 0x555555,
      linewidth: 2,
      depthTest: false,
    });
    const line = new Line2(lineGeometry, lineMaterial);
    line.visible = false;
    this.scene.add(line);

    this.handles.set(annotation, { sphere, line });

    // Drag: snap to point cloud and update annotation position
    sphere.addEventListener('drag', (e) => {
      const I = Utils.getMousePointCloudIntersection(
        e.drag.end,
        this.viewer.scene.getActiveCamera(),
        this.viewer,
        this.viewer.scene.pointclouds,
        { pickClipped: true }
      );
      if (I) {
        const delta = new THREE.Vector3().subVectors(I.location, annotation.position);
        annotation.offset.sub(delta);
        annotation.position.copy(I.location);
        sphere.position.copy(I.location);
      }
    });

    sphere.addEventListener('drop', (e) => {
      sphere.material.emissive.setHex(0x000000);
      this.viewer.scene.dispatchEvent({
        type: 'annotation_moved',
        annotation: annotation,
      });
    });

    // Hover: highlight with emissive (same as measurements)
    sphere.addEventListener('mouseover', (e) => {
      e.object.material.emissive.setHex(0xaaaa55);
    });
    sphere.addEventListener('mouseleave', (e) => {
      e.object.material.emissive.setHex(0x000000);
    });
  }

  removeHandle(annotation) {
    const handle = this.handles.get(annotation);
    if (!handle) return;

    const { sphere, line } = handle;
    this.scene.remove(sphere);
    this.scene.remove(line);
    sphere.material.dispose();
    line.material.dispose();
    line.geometry.dispose();
    this.handles.delete(annotation);
  }

  // Helper: detect mobile device
  isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (typeof window.orientation !== 'undefined') ||
           (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
           ('ontouchstart' in window && navigator.maxTouchPoints > 1);
  }

  startInsertion(args = {}) {
    let domElement = this.viewer.renderer.domElement;
    let annotation;
    const t = (key, fallback) => (typeof i18n !== 'undefined') ? i18n.t(key) : fallback;

    if (Object.keys(args).length === 0) {
      annotation = new Annotation({
        position: [0.0, 0.0, 0.0],
        title: t('annotations.name_annotation', 'Annotation'),
        description: '',
      });
    } else {
      let cameraPosition = args.cameraPosition || null;
      let cameraTarget = args.cameraTarget || null;
      annotation = new Annotation({
        position: [0.0, 0.0, 0.0],
        title: t('annotations.name_camera_annotation', 'Camera Position'),
        description: '',
        cameraPosition: cameraPosition,
        cameraTarget: cameraTarget,
      });
    }

    this.viewer.dispatchEvent({ type: 'cancel_insertions' });

    this.dispatchEvent({
      type: 'start_inserting_annotation',
      annotation: annotation,
    });

    const annotations = this.viewer.scene.annotations;

    // Preview sphere that snaps to point cloud and follows the cursor
    const sphere = new THREE.Mesh(
      this.sphereGeometry,
      new THREE.MeshLambertMaterial({ color: 0x555555, emissive: 0x000000 })
    );
    sphere.visible = false;
    this.scene.add(sphere);

    let callbacks = {
      cancel: null,
      finish: null,
    };

    // Sphere follows mouse via InputHandler drag events
    sphere.addEventListener('drag', (e) => {
      const camera = this.viewer.scene.getActiveCamera();
      const I = Utils.getMousePointCloudIntersection(
        e.drag.end,
        camera,
        this.viewer,
        this.viewer.scene.pointclouds,
        { pickClipped: false }
      );
      if (I) {
        sphere.position.copy(I.location);
        sphere.visible = true;

        // Scale sphere to constant screen size, matching MeasuringTool
        const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
        const distance = camera.position.distanceTo(sphere.position);
        const pr = Utils.projectedRadius(1, camera, distance, renderAreaSize.width, renderAreaSize.height);
        const scale = 15 / pr;
        sphere.scale.set(scale, scale, scale);
      }
    });

    sphere.addEventListener('drop', (e) => {
      if (sphere.visible) {
        annotation.position.copy(sphere.position);
        annotations.add(annotation);
        callbacks.finish();
      }
    });

    const cleanup = () => {
      this.scene.remove(sphere);
      sphere.material.dispose();
      domElement.removeEventListener('mouseup', onMouseUp, true);
      domElement.removeEventListener('touchend', onTouchEnd, true);
      domElement.removeEventListener('keydown', onKeyDown, true);
      this.viewer.removeEventListener('cancel_insertions', callbacks.cancel);
    };

    callbacks.cancel = () => {
      cleanup();
      this.viewer.measureModeEnabled = false;
      this.viewer.inputHandler.measureModeEnabled = false;
    };

    callbacks.finish = () => {
      cleanup();
      this.viewer.measureModeEnabled = false;
      this.viewer.inputHandler.measureModeEnabled = false;
      this.viewer.dispatchEvent({ type: 'annotation_completed', annotation });
    };

    // Cancel on right-click (capture so it fires before InputHandler's drop)
    const onMouseUp = (e) => {
      if (e.button === THREE.MOUSE.RIGHT) callbacks.cancel();
    };

    // Touch: tap to place
    const onTouchEnd = (e) => {
      if (!e.changedTouches || e.changedTouches.length !== 1) return;
      const rect = domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        e.changedTouches[0].pageX - rect.left,
        e.changedTouches[0].pageY - rect.top
      );
      const I = Utils.getMousePointCloudIntersection(
        mouse,
        this.viewer.scene.getActiveCamera(),
        this.viewer,
        this.viewer.scene.pointclouds,
        { pickClipped: false }
      );
      if (I) {
        annotation.position.copy(I.location);
        annotations.add(annotation);
        callbacks.finish();
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') callbacks.cancel();
    };

    domElement.addEventListener('mouseup', onMouseUp, true);
    domElement.addEventListener('touchend', onTouchEnd, true);
    domElement.addEventListener('keydown', onKeyDown, true);
    this.viewer.addEventListener('cancel_insertions', callbacks.cancel);

    // Start dragging the preview sphere immediately
    this.viewer.inputHandler.startDragging(sphere);

    return annotation;
  }

  insertCameraPosition() {
    const t = (key, fallback) => (typeof i18n !== 'undefined') ? i18n.t(key) : fallback;
    const camera = this.viewer.scene.getActiveCamera();
    const pivot = this.viewer.scene.view.getPivot();

    const annotation = new Annotation({
      position: camera.position.toArray(),
      title: t('annotations.name_camera_annotation', 'Camera Position'),
      description: '',
      cameraPosition: camera.position.toArray(),
      cameraTarget: pivot.toArray(),
    });

    this.viewer.scene.annotations.add(annotation);
    annotation.display = false;
    this.viewer.dispatchEvent({ type: 'annotation_completed', annotation });

    return annotation;
  }

  continueInsertion(annotation) {

    let domElement = this.viewer.renderer.domElement;
    const annotations = this.viewer.scene.annotations;

    let callbacks = {
      cancel: null,
      finish: null,
    };

    // Track if event was already processed to prevent double handling
    let lastProcessedTime = 0;
    const DEBOUNCE_MS = 100;

    // Get mouse position from event (works for both mouse and touch)
    const getMousePosition = (e) => {
      let rect = domElement.getBoundingClientRect();
      let x, y;

      if (e.changedTouches && e.changedTouches.length > 0) {
        // Touch event
        x = e.changedTouches[0].pageX - rect.left;
        y = e.changedTouches[0].pageY - rect.top;
      } else {
        // Mouse event
        x = e.clientX - rect.left;
        y = e.clientY - rect.top;
      }

      return new THREE.Vector2(x, y);
    };

    let insertionCallback = (e) => {
      // Prevent double processing of touch + emulated mouse events
      const now = Date.now();
      if (now - lastProcessedTime < DEBOUNCE_MS) {
        return;
      }

      // Handle cancel (right click or Escape)
      if (e.button === THREE.MOUSE.RIGHT || e.key === 'Escape') {
        lastProcessedTime = now;
        callbacks.cancel();
        return;
      }

      // Handle touch events - tap to place
      if (e.type === 'touchend') {
        if (e.changedTouches && e.changedTouches.length === 1) {
          lastProcessedTime = now;

          let mouse = getMousePosition(e);
          let I = Utils.getMousePointCloudIntersection(
            mouse,
            this.viewer.scene.getActiveCamera(),
            this.viewer,
            this.viewer.scene.pointclouds,
            { pickClipped: true }
          );

          if (I) {
            annotation.position.copy(I.location);
            callbacks.finish();
          }
          return;
        }
      }

      // Handle mouse events - click to place
      if (e.button === THREE.MOUSE.LEFT) {
        lastProcessedTime = now;

        let mouse = getMousePosition(e);
        let I = Utils.getMousePointCloudIntersection(
          mouse,
          this.viewer.scene.getActiveCamera(),
          this.viewer,
          this.viewer.scene.pointclouds,
          { pickClipped: true }
        );

        if (I) {
          annotation.position.copy(I.location);
          callbacks.finish();
        }
      }
    };

    callbacks.cancel = (e) => {
      annotations.remove(annotation);
      domElement.removeEventListener('mouseup', insertionCallback, true);
      domElement.removeEventListener('touchend', insertionCallback, true);
      domElement.removeEventListener('keydown', insertionCallback, true);

      // Disable measure mode to return to navigation
      this.viewer.measureModeEnabled = false;
      this.viewer.inputHandler.measureModeEnabled = false;
    };

    callbacks.finish = (e) => {
      domElement.removeEventListener('mouseup', insertionCallback, true);
      domElement.removeEventListener('touchend', insertionCallback, true);
      domElement.removeEventListener('keydown', insertionCallback, true);
      this.viewer.scene.dispatchEvent({
        type: 'annotation_moved',
        annotation: annotation,
      });

      // Disable measure mode to return to navigation
      this.viewer.measureModeEnabled = false;
      this.viewer.inputHandler.measureModeEnabled = false;

      // Dispatch event so mobile toolbar can update its state
      this.viewer.dispatchEvent({
        type: 'annotation_completed',
        annotation: annotation
      });
    };

    domElement.addEventListener('mouseup', insertionCallback, true);
    domElement.addEventListener('touchend', insertionCallback, true);
    domElement.addEventListener('keydown', insertionCallback, true);
  }

  update() {
    const camera = this.viewer.scene.getActiveCamera();
    const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
    const clientWidth = renderAreaSize.width;
    const clientHeight = renderAreaSize.height;

    // Update light to follow camera (same as MeasuringTool)
    this.light.position.copy(camera.position);
    this.lightTargetObject.position.copy(camera.position).add(camera.getWorldDirection(new THREE.Vector3()));

    // Scale handle spheres, update connecting lines, sync positions
    for (const [annotation, handle] of this.handles) {
      const { sphere, line } = handle;

      // Sync visibility with annotation display state
      const isVisible = annotation._visible && annotation._display;
      sphere.visible = isVisible;

      // Sync sphere position with annotation
      sphere.position.copy(annotation.position);

      const distance = camera.position.distanceTo(
        sphere.getWorldPosition(new THREE.Vector3())
      );
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 15 / pr;
      sphere.scale.set(scale, scale, scale);
      sphere.updateMatrixWorld(true);

      // Update connecting line
      const offsetLen = annotation.offset.length();
      line.visible = isVisible && offsetLen > 0.001;
      if (line.visible) {
        const end = new THREE.Vector3().addVectors(annotation.position, annotation.offset);
        line.position.copy(annotation.position);
        const localEnd = end.clone().sub(annotation.position);
        line.geometry.setPositions([0, 0, 0, localEnd.x, localEnd.y, localEnd.z]);
        line.geometry.computeBoundingSphere();
        line.computeLineDistances();
        line.material.resolution.set(clientWidth, clientHeight);
      }
    }
  }

  render() {
    this.viewer.renderer.render(this.scene, this.viewer.scene.getActiveCamera());
  }
}
